/**
 * Bundle Optimizer
 * Dynamic imports and code splitting for better performance
 */

import React from 'react';

/**
 * Lazy load components with loading states
 */
export const lazyLoadComponent = (importFn, fallback = null) => {
  return React.lazy(() => {
    return importFn().catch(error => {
      return { default: fallback || (() => <div>Failed to load component</div>) };
    });
  });
};

/**
 * Dynamic import with retry logic
 */
export const dynamicImport = async (importFn, retries = 3) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await importFn();
    } catch (error) {
      if (i === retries - 1) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
};

/**
 * Preload critical components
 */
export const preloadCriticalComponents = () => {
  const criticalComponents = [
    () => import('../components/ads/AdsDialog'),
    () => import('../components/AddProductModal'),
    () => import('../components/EditProductModal'),
    () => import('../pages/Dashboard'),
    () => import('../pages/InventorySimplified')
  ];
  
  criticalComponents.forEach(importFn => {
    importFn().catch(() => {});
  });
};

/**
 * Lazy load pages with route-based splitting
 */
export const lazyLoadPage = (pageName) => {
  const pageMap = {
    dashboard: () => import('../pages/Dashboard'),
    inventory: () => import('../pages/InventorySimplified'),
    sales: () => import('../pages/Sales'),
    purchases: () => import('../pages/Purchases'),
    orders: () => import('../pages/Orders'),
    customers: () => import('../pages/Customers'),
    suppliers: () => import('../pages/Suppliers'),
    reports: () => import('../pages/Reports'),
    settings: () => import('../pages/Settings'),
    admin: () => import('../pages/AdminDashboard')
  };
  
  const importFn = pageMap[pageName];
  if (!importFn) {
    throw new Error(`Unknown page: ${pageName}`);
  }
  
  return React.lazy(importFn);
};

/**
 * Lazy load modals
 */
export const lazyLoadModal = (modalName) => {
  const modalMap = {
    addProduct: () => import('../components/AddProductModal'),
    editProduct: () => import('../components/EditProductModal'),
    addPurchase: () => import('../components/AddPurchaseModal'),
    editPurchase: () => import('../components/EditPurchaseModal'),
    addOrder: () => import('../components/AddOrderModal'),
    editOrder: () => import('../components/EditOrderModal'),
    addSupplier: () => import('../components/AddSupplierModal'),
    addCustomer: () => import('../components/AddCustomerModal'),
    cashRegister: () => import('../components/cash-register/NewCashRegisterModal'),
    excelImport: () => import('../components/ExcelImportModal')
  };
  
  const importFn = modalMap[modalName];
  if (!importFn) {
    throw new Error(`Unknown modal: ${modalName}`);
  }
  
  return React.lazy(importFn);
};

/**
 * Lazy load utilities
 */
export const lazyLoadUtility = (utilityName) => {
  const utilityMap = {
    dateUtils: () => import('../utils/dateUtils'),
    constants: () => import('../utils/constants'),
    translations: () => import('../utils/constants'),
    lotSystem: () => import('../utils/lotSystem'),
    stockCalculation: () => import('../services/stockCalculationService'),
    profitAnalysis: () => import('../services/profitAnalysisService'),
    instantCache: () => import('../services/instantCache')
  };
  
  const importFn = utilityMap[utilityName];
  if (!importFn) {
    throw new Error(`Unknown utility: ${utilityName}`);
  }
  
  return importFn();
};

/**
 * Lazy load workers - DISABLED (Master Worker Pool deaktiviert)
 */
export const lazyLoadWorker = (workerName) => {
  // Master Worker Pool deaktiviert - return null
  console.log('⚠️ [BUNDLE OPTIMIZER] Worker loading disabled');
  return Promise.resolve(null);
};

/**
 * Preload routes based on user behavior
 */
export const preloadRoutes = (currentRoute) => {
  const routePreloadMap = {
    dashboard: ['inventory', 'sales'],
    inventory: ['sales', 'purchases'],
    sales: ['inventory', 'customers'],
    purchases: ['inventory', 'suppliers'],
    orders: ['inventory', 'customers'],
    customers: ['sales', 'orders'],
    suppliers: ['purchases', 'inventory'],
    reports: ['dashboard', 'sales', 'purchases']
  };
  
  const routesToPreload = routePreloadMap[currentRoute] || [];
  
  routesToPreload.forEach(route => {
    try {
      lazyLoadPage(route);
    } catch (error) {
      // Failed to preload route
    }
  });
};

/**
 * Bundle analyzer for development
 */
export const analyzeBundle = () => {
  if (process.env.NODE_ENV !== 'development') return;
  
  const modules = [];
  
  // Analyze loaded modules
  if (window.__webpack_require__) {
    const require = window.__webpack_require__;
    const modules = require.cache || {};
    
    Object.keys(modules).forEach(moduleId => {
      const module = modules[moduleId];
      if (module && module.exports) {
        modules.push({
          id: moduleId,
          size: JSON.stringify(module.exports).length,
          loaded: true
        });
      }
    });
  }
  
  return modules;
};

/**
 * Performance budget checker
 */
export const checkPerformanceBudget = () => {
  const budget = {
    maxBundleSize: 2 * 1024 * 1024, // 2MB
    maxInitialLoad: 1000, // 1 second
    maxTimeToInteractive: 3000, // 3 seconds
    maxFirstContentfulPaint: 1500 // 1.5 seconds
  };
  
  const metrics = {
    bundleSize: 0, // Would be calculated from actual bundle
    initialLoad: performance.timing.loadEventEnd - performance.timing.navigationStart,
    timeToInteractive: 0, // Would be calculated from actual metrics
    firstContentfulPaint: 0 // Would be calculated from actual metrics
  };
  
  const violations = [];
  
  if (metrics.bundleSize > budget.maxBundleSize) {
    violations.push(`Bundle size exceeds budget: ${metrics.bundleSize} > ${budget.maxBundleSize}`);
  }
  
  if (metrics.initialLoad > budget.maxInitialLoad) {
    violations.push(`Initial load exceeds budget: ${metrics.initialLoad} > ${budget.maxInitialLoad}`);
  }
  
  // Performance budget violations handled silently
  
  return {
    budget,
    metrics,
    violations
  };
};

/**
 * Initialize bundle optimization
 */
export const initializeBundleOptimization = () => {
  // Preload critical components
  preloadCriticalComponents();
  
  // Check performance budget
  checkPerformanceBudget();
  
  // Analyze bundle in development
  if (process.env.NODE_ENV === 'development') {
    analyzeBundle();
  }
  
  // Bundle optimization initialized
};
